import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Trash2, RefreshCw } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtEUR, fmtEUR2, fmtDate } from "@/lib/format";
import { CARD, Label, Loader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatementSheet } from "@/components/tiers/StatementSheet";
import { Relances } from "@/components/tiers/Relances";

function UploadCard({ onDone }) {
  const [kind, setKind] = useState("secu");
  const [payer, setPayer] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const send = async (files) => {
    if (kind === "mutuelle" && !payer.trim()) { if (ref.current) ref.current.value = ""; toast.error("Indiquez le nom de la mutuelle"); return; }
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", kind); fd.append("payer", payer);
      try { await api.post("/tiers-payant/statements", fd); ok += 1; } catch (e) { toast.error(`${f.name} : ${apiErr(e)}`); }
    }
    setBusy(false); if (ref.current) ref.current.value = "";
    if (ok) { toast.success(`${ok} relevé(s) envoyé(s), lecture en cours`); onDone(); }
  };
  return (
    <div className={`${CARD} space-y-4 p-6`} data-testid="tp-upload">
      <Label>Importer un relevé</Label>
      <div className="flex flex-wrap gap-3">
        <Select value={kind} onValueChange={setKind}><SelectTrigger className="w-72" data-testid="tp-kind"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="secu">Sécurité sociale</SelectItem><SelectItem value="mutuelle">Mutuelle</SelectItem><SelectItem value="logiciel">Export logiciel métier (Orthalis, Logos « Payée »)</SelectItem></SelectContent></Select>
        {kind === "mutuelle" && <Input placeholder="Nom de la mutuelle" value={payer} onChange={(e) => setPayer(e.target.value)} className="w-56" data-testid="tp-payer" />}
        <input ref={ref} type="file" multiple accept=".pdf,.csv,.xls,.xlsx" className="hidden" onChange={(e) => send([...e.target.files])} data-testid="tp-file-input" />
        <Button onClick={() => ref.current?.click()} disabled={busy} className="rounded-full bg-[#065F46] hover:bg-[#054d39]" data-testid="tp-upload-btn"><Upload className="mr-1.5 h-4 w-4" />{busy ? "Envoi…" : "Choisir les fichiers"}</Button>
      </div>
      <p className="text-xs text-gray-500">{kind === "logiciel" ? "Export Excel/CSV de votre logiciel (colonnes Patient, Date FSE, Montant AMO, Montant AMC, Reste à payer). Reste à payer = 0 → Sécu (AMO) et mutuelle (AMC) reçues ; reste = AMC → seule la Sécu est reçue ; reste = AMO → seule la mutuelle. Rapprochement par patient + date des soins + montant, puis par patient + montant." : "PDF (Ameli Pro, portails mutuelles), CSV ou Excel. Chaque versement est comparé aux paiements attendus sur le nom du patient et le montant exact au centime (la date des soins n'est pas comparée). Si un patient apparaît sur plusieurs lignes, leur somme est comparée au total attendu. Correspondance → marqué reçu automatiquement ; les autres cas restent à vérifier ci-dessous."}</p>
    </div>
  );
}

function Pending({ data }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="tp-pending">
      {[["secu", "Sécu en attente"], ["mutuelle", "Mutuelles en attente"]].map(([k, l]) => (
        <div key={k} className={`${CARD} p-5`}><p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{l}</p>
          <p className="mt-1 font-display text-2xl font-bold tabular-nums" data-testid={`tp-pending-${k}`}>{fmtEUR(data[k].amount)}</p>
          <p className="text-xs text-gray-500">{data[k].count} versement(s) · {data[k].late} en retard</p></div>))}
    </div>
  );
}

const ST = { pending: "Lecture…", matching: "Rapprochement en cours…", failed: "Échec de lecture", done: null };

function StatementRow({ s, onOpen, onDelete }) {
  const st = s.stats || {};
  const todo = (st.ecart || 0) + (st.ambigu || 0) + (st.introuvable || 0) + (st.partiel || 0);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5" data-testid="tp-statement-row">
      <button type="button" onClick={() => onOpen(s.id)} disabled={s.extraction !== "done"} data-testid="tp-statement-open" className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <FileText className="h-5 w-5 shrink-0 text-[#B08D57]" />
        <div className="min-w-0"><p className="truncate font-semibold">{s.payer || (s.kind === "secu" ? "Sécurité sociale" : "Mutuelle")} · {s.original_filename}</p>
          <p className="text-xs text-gray-400">{ST[s.extraction] || `${st.total} versement(s) · ${fmtEUR2(st.amount)} · importé le ${fmtDate(s.created_at)}`}{s.import_info ? (s.import_info.format === "logos" ? ` · ${s.import_info.rows} lignes Logos lues, ${s.import_info.rows - s.import_info.unpaid} payées, ${s.import_info.unpaid} non payées` : ` · ${s.import_info.rows} FSE lues, ${s.import_info.unpaid} non payées, ${s.import_info.partial} paiement(s) partiel(s) ignoré(s)`) : ""}{s.error ? ` — ${s.error}` : ""}</p></div>
      </button>
      {s.extraction === "done" && <div className="flex gap-1.5 text-[11px] font-semibold">
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">{(st.recu || 0) + (st.manuel || 0)} reçu(s)</span>
        {todo > 0 && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">{todo} à vérifier</span>}</div>}
      {(s.extraction === "pending" || s.extraction === "matching") && <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />}
      <button type="button" aria-label="Supprimer le relevé" onClick={() => onDelete(s)} data-testid="tp-statement-delete" className="rounded-lg p-1 text-gray-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export default function TiersPayant() {
  const [rows, setRows] = useState(null);
  const [pend, setPend] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    api.get("/tiers-payant/statements").then((r) => setRows(r.data));
    api.get("/tiers-payant/pending").then((r) => setPend(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rows?.some((s) => s.extraction === "pending" || s.extraction === "matching")) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [rows, load]);
  const del = (s) => {
    if (!window.confirm("Supprimer ce relevé ? Les versements qu'il avait marqués reçus redeviendront en attente.")) return;
    api.delete(`/tiers-payant/statements/${s.id}`).then(() => { toast.success("Relevé supprimé"); load(); }).catch((e) => toast.error(apiErr(e)));
  };
  return (
    <div className="space-y-6" data-testid="tiers-payant-page">
      <div><Label>Finances</Label><h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Rapprochement tiers payant</h1>
        <p className="mt-1 text-sm text-gray-500">Importez vos relevés Sécurité sociale et mutuelles : les versements attendus correspondants passent automatiquement en « reçu ».</p></div>
      {pend ? <Pending data={pend} /> : <Loader />}
      <UploadCard onDone={load} />
      <div><div className="mb-2 flex flex-wrap items-center justify-between gap-2"><Label>Relevés importés</Label>
        {rows?.some((s) => s.extraction === "done") && <Button variant="outline" size="sm" data-testid="tp-rematch-all" disabled={rows.some((s) => s.extraction === "matching")}
          onClick={() => api.post("/tiers-payant/rematch-all").then((r) => { toast.success(`Rapprochement relancé sur ${r.data.statements} relevé(s)`); setTimeout(load, 800); }).catch((e) => toast.error(apiErr(e)))}>
          <RefreshCw className="mr-1.5 h-4 w-4" />Relancer tous les relevés</Button>}</div>
        {!rows ? <Loader /> : (
          <div className={`${CARD} divide-y divide-gray-100`} data-testid="tp-statements">
            {rows.map((s) => <StatementRow key={s.id} s={s} onOpen={setOpen} onDelete={del} />)}
            {!rows.length && <p className="p-8 text-center text-sm text-gray-400">Aucun relevé importé pour l'instant.</p>}
          </div>)}
      </div>
      <Relances />
      <StatementSheet sid={open} onClose={() => setOpen(null)} onChange={load} />
    </div>
  );
}
